import EXIF from 'exif-js';
import { fileCenter, FileInfo, FileStatus } from "@/common/services/FileCenter";
import type { IFileCenterContext, WH } from '@/common/components/FileCenterProvider';
import { Subscription } from '@/common/constants/interfaces';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { DrawContext, drawWatermark } from '@/common/utils/WatermarkCanvas';
import { reportRequestError } from './report';

interface FontProps {
    fontFamily: string;
    fontSize: number;
    isItalic: boolean;
    isBold: boolean;
}

interface IPoint {
    x: number;
    y: number;
}

interface Rect {
    left: number;
    top: number;
    width: number;
    height: number;
}

function initMeasureText() {
    const div = document.createElement('div')
    div.id = 'measure-text'
    div.innerHTML = ""
    div.style.cssText = "visibility: hidden;display: 'inline-block';position:absolute;white-space: nowrap;line-height: 1;"
    document.body.appendChild(div)
}

initMeasureText()


function getImageRatio(originWidth: number, originHeight: number, containerWidth: number, containerHeight: number) {
    if (originWidth <= containerWidth && originHeight <= containerHeight) {
        return 1;
    }

    const widthRatio = containerWidth / originWidth;
    const heightRatio = containerHeight / originHeight;
    return Math.min(widthRatio, heightRatio)
}

function getTextWH(text: string, font: FontProps) {
    const textMetrics = measureTextDimensions(text, font)
    if (!textMetrics) {
        return { width: 0, height: 0, textMetrics: { fontBoundingBoxAscent: 0, fontBoundingBoxDescent: 0, actualBoundingBoxDescent: 0, actualBoundingBoxAscent: 0 } }
    }
    // m.fontBoundingBoxAscent + m.fontBoundingBoxDescent 为行高line-height
    return { width: textMetrics.width, height: textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent, textMetrics }
}

function measureTextDimensions(text: string, fontProps: FontProps) {
    // 创建一个隐藏的canvas元素
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }
    const { fontFamily, fontSize, isItalic, isBold } = fontProps
    // 设置字体
    ctx.font = `${isItalic ? 'italic' : ''} ${isBold ? 'bold' : ''} ${fontSize}px ${fontFamily},serif`;
    // 测量宽度
    const metrics = ctx.measureText(text);
    return metrics;
}

function createCanvas() {
    const canvas = document.createElement("canvas");
    return canvas
}

function resizeCanvas(canvas: HTMLCanvasElement, width: number, height: number) {
    canvas.width = width
    canvas.height = height
}

function canvas2Img(canvas: HTMLCanvasElement, imgType: string, quality: number = 1) {
    return new Promise<string>((resolve, reject) => {
        canvas.toBlob(function (blob) {
            if (!blob) {
                resolve('')
                return
            }
            var url = URL.createObjectURL(blob);
            resolve(url)
        }, imgType, quality);
    })
}

function canvas2Blob(canvas: HTMLCanvasElement, imgType: string, quality: number = 1) {
    return new Promise<Blob | null>((resolve, reject) => {
        canvas.toBlob(function (blob) {
            if (!blob) {
                resolve(null)
                return
            }
            resolve(blob)
        }, imgType, quality);
    })
}

function download(url: string, fileInfo: FileInfo) {
    // 创建一个临时链接并模拟点击下载
    var a = document.createElement('a');
    a.href = url
    a.download = fileInfo.file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    fileInfo.setStatus(FileStatus.Success)
}

function throttle(func: any, delay: number) {
    let lastTime = 0;

    return (args: any) => {
        const now = Date.now();

        if (now - lastTime >= delay) {
            lastTime = now;
            func(args);
        }
    };
}

async function collectCanvas(fileCenterContext: IFileCenterContext, needSerial: boolean, cb: (canvas: HTMLCanvasElement, pendingFileInfo: FileInfo) => void, finishedCb?: () => void): Promise<void> {
    const canvas = createCanvas();
    if (!canvas) {
        return
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    const { imageEditViewWH, watermarks } = fileCenterContext
    const fileList = fileCenter.getFileInfoList();

    for (let fileInfo of fileList) {
        if (fileInfo.status !== FileStatus.Pending) {
            // 如果文件不是Pending，则表明用户取消了任务，此时任务状态会变成Ready，不再处理
            continue;
        }
        const { image: img } = fileInfo;
        const { width, height } = img;
        const ratio = getImageRatio(width, height, imageEditViewWH.width, imageEditViewWH.height)

        // 导出或替换图片时，不存在css像素,dpr都为1
        canvas.width = width;
        canvas.height = height;
        // 当你设置 <canvas> 元素的 width 和 height 属性时，会重置画布的内容,并且重置绘图上下文（ctx）

        ctx.drawImage(img, 0, 0, width, height);

        const drawContex: DrawContext = {
            canvas,
            ctx,
            width,
            height,
            dpr: window.devicePixelRatio,
            ratio,
            isCollect: true,
            fileInfo,
        }

        watermarks.forEach(watermark => { drawWatermark(watermark, drawContex) })

        // 批量替换不能用await来执行
        if (needSerial) {
            await cb(canvas, fileInfo)
        } else {
            cb(canvas, fileInfo)
        }
    }
    finishedCb && finishedCb()
}

function isSupportFileSystemHandle() {
    return (window as any).showOpenFilePicker && (window as any).DataTransferItem.prototype.getAsFileSystemHandle;
}

// 添加这个新函数来获取图片的拍摄时间
async function getImageTakenTime(file: File): Promise<Date> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = function (e) {
            if (!e.target?.result) {
                resolve(new Date(file.lastModified));
                return;
            }

            try {
                const exif = EXIF.readFromBinaryFile(e.target.result);
                if (exif?.DateTimeOriginal) {
                    // EXIF 日期格式通常为 "YYYY:MM:DD HH:mm:ss"
                    const [date, time] = exif.DateTimeOriginal.split(' ');
                    const [year, month, day] = date.split(':');
                    const [hour, minute, second] = time.split(':');
                    resolve(new Date(year, month - 1, day, hour, minute, second));
                } else {
                    // 如果没有拍摄时间，返回文件的最后修改时间
                    resolve(new Date(file.lastModified));
                }
            } catch (error) {
                console.error('Error reading EXIF data:', error);
                // 发生错误时返回文件的最后修改时间
                resolve(new Date(file.lastModified));
            }
        };
        reader.readAsArrayBuffer(file);
    });
}


function getString(waterMarkTemplate: string, date: Date): string {
    const formatMap: { [key: string]: string } = {
        'YYYY': date.getFullYear().toString(),
        'MM': (date.getMonth() + 1).toString().padStart(2, '0'),
        'DD': date.getDate().toString().padStart(2, '0'),
        'HH': date.getHours().toString().padStart(2, '0'),
        'mm': date.getMinutes().toString().padStart(2, '0'),
        'ss': date.getSeconds().toString().padStart(2, '0')
    };

    return waterMarkTemplate.replace(/\{([^}]+)\}/g, (match, p1) => {
        let result = p1;
        for (const [placeholder, value] of Object.entries(formatMap)) {
            result = result.replace(placeholder, value);
        }
        return result;
    });
}

type RequestMethod = 'GET' | 'POST';

interface RequestParams {
    url: string;
    method: RequestMethod;
    params?: Record<string, any>;
    body?: Record<string, any>;
    headers?: HeadersInit;
}


async function request<T>({ url, method, params, body, headers }: RequestParams): Promise<T> {
    const token = localStorageUtil.getItem('token');

    // 构建请求头
    const defaultHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };

    // 处理 GET 请求的 URL 参数
    if (method === 'GET' && params) {
        const urlParams = new URLSearchParams(params).toString();
        url += `?${urlParams}`;
    }

    // 构建 fetch 选项
    const options: RequestInit = {
        method,
        headers: {
            ...defaultHeaders,
            ...headers,
        },
    };

    // 处理 POST 请求的 body 参数
    if (method === 'POST' && body) {
        options.body = JSON.stringify(body);
    }

    try {
        // 发送请求并返回响应
        const response = await fetch(url, options);
        const res = await response.json();
        if (!res) {
            const errMsg = '请求错误: res is null';
            reportRequestError(url, params, errMsg);
            throw new Error(errMsg);
        }
        if (!res.success) {
            const errMsg = `请求错误: res.success 为false, ${res.message || '未知错误'}`;
            reportRequestError(url, params, errMsg);
            throw new Error(errMsg);
        }
        return res.data;
    } catch (error) { // 捕获网络错误
        const errMsg = `请求错误: ${error}`;
        reportRequestError(url, params, errMsg);
        throw error;
    }

}

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getFileStatusText(fileStatus: FileStatus): string {
    switch (fileStatus) {
        case FileStatus.Pending:
            return '处理中';
        case FileStatus.Success:
            return '成功';
        case FileStatus.Failure:
            return '失败';
        default:
            return '等待处理';
    }
}

// 获取旋转后的元素四个顶点坐标(相对于浏览器)
function getRotatedElementCorners(element: HTMLDivElement) {
    // 获取包围盒
    const rect = element.getBoundingClientRect();

    // 获取包围盒宽高
    const width = rect.width;
    const height = rect.height;

    // 获取中心点
    const cx = rect.left + width / 2;
    const cy = rect.top + height / 2;

    // 获取 transform 属性
    const style = window.getComputedStyle(element);
    const transform = style.transform;

    if (transform === "none") {
        // 没有旋转的情况，直接返回边框
        return [
            { x: rect.left, y: rect.top },
            { x: rect.right, y: rect.top },
            { x: rect.right, y: rect.bottom },
            { x: rect.left, y: rect.bottom },
        ];
    }

    // 解析 transform 矩阵
    const res = transform.match(/matrix\(([^)]+)\)/)
    if (!res) {
        return [
            { x: rect.left, y: rect.top },
            { x: rect.right, y: rect.top },
            { x: rect.right, y: rect.bottom },
            { x: rect.left, y: rect.bottom },
        ];
    }
    const matrix = res[1].split(',').map(parseFloat);
    const [a, b, c, d, e, f] = matrix;

    const originWdith = element.offsetWidth;
    const originHeight = element.offsetHeight;
    // 计算原始顶点（未旋转）
    const corners = [
        { x: -originWdith / 2, y: -originHeight / 2 }, // 左上角
        { x: originWdith / 2, y: -originHeight / 2 },  // 右上角
        { x: originWdith / 2, y: originHeight / 2 },   // 右下角
        { x: -originWdith / 2, y: originHeight / 2 },  // 左下角
    ];


    // 应用旋转矩阵，计算旋转后的顶点坐标
    return corners.map(({ x, y }) => ({
        x: cx + a * x + c * y,
        y: cy + b * x + d * y,
    }));
}

/**
 * 在屏幕坐标系下,获取一个矩形旋转后的四个顶点坐标
 * @param rect 矩形四个顶点坐标
 * @param angle 要旋转的角度，为-180到180之间的数
 * @returns 旋转后的四个顶点坐标
 */
function getRotatedRectCorners(rect: IPoint[], angle: number): IPoint[] {
    const radians = angle * (Math.PI / 180);
    const centerX = (rect[0].x + rect[2].x) / 2;
    const centerY = (rect[0].y + rect[2].y) / 2;

    return rect.map(point => {
        const x = point.x - centerX;
        const y = point.y - centerY;

        const rotatedX = x * Math.cos(radians) - y * Math.sin(radians);
        const rotatedY = x * Math.sin(radians) + y * Math.cos(radians);

        return {
            x: rotatedX + centerX,
            y: rotatedY + centerY
        };
    });
}
const formatDate = (date: Date | null) => {
    if (!date) {
        return '';
    }
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    };
    return date.toLocaleString('zh-CN', options).replace(/\//g, '-').replace(',', '');
};

const isSubscriptionAvailable = (subscription: Subscription | null) => {
    if (!subscription) {
        return false;
    }

    const now = new Date().getTime();
    const subscriptionEndDate = new Date(subscription.subscription_end);
    return now < subscriptionEndDate.getTime();
}

/**
 * 取模运算
 * @param a 
 * @param 模数
 * @returns 
 */
const mod = (a: number, b: number) => {
    return ((a % b) + b) % b;
}

export {
    formatFileSize,
    request,
    getImageRatio,
    getTextWH,
    createCanvas,
    resizeCanvas,
    canvas2Img,
    canvas2Blob,
    download,
    throttle,
    collectCanvas,
    isSupportFileSystemHandle,
    getString,
    getImageTakenTime,
    getFileStatusText,
    getRotatedElementCorners,
    getRotatedRectCorners,
    formatDate,
    isSubscriptionAvailable,
    mod,
};
export type { IPoint, Rect };
